// 2026-11-03: LS-100 review sends one complete configuration through the Longshade inquiry endpoint.
'use strict';

// Keep the reviewed configuration available to the single inquiry submission handler.
(() => {
  const root = document.querySelector('#ls100-product');
  if (!root) return;
  const form = root.querySelector('#shade-form');
  const panels = [...root.querySelectorAll('[data-step]')];
  const stepButtons = [...root.querySelectorAll('[data-step-link]')];
  const $ = selector => root.querySelector(selector);
  const options = {
    space_type: ['Balcony', 'Patio', 'Window', 'Other', 'Not sure'],
    layout: ['Single opening', 'L-shaped', 'Three-sided', 'Not sure'],
    measurement_status: [
      { value: 'approximate', label: 'I have approximate measurements', note: 'Add the width and height of each opening' },
      { value: 'home_measurement', label: 'I need a home measurement', note: 'Our team can help confirm the dimensions' }
    ],
    shade_priority: ['Outward view', 'More privacy', 'Blackout', 'Help me choose'],
    fabric_id: [
      { value: 'blackout', label: 'Blackout fabric', note: '0% openness · three colour options' },
      { value: 'help', label: 'Help me choose', note: 'Get a recommendation for your space' }
    ],
    fabric_colour_id: [
      { value: 'NP23004060SP', label: 'Black & Brown', note: 'NP23004060SP', swatch: 'brown' },
      { value: 'NP23005050SP', label: 'Grey', note: 'NP23005050SP', swatch: 'grey' },
      { value: 'NP23004040SP', label: 'Black', note: 'NP23004040SP', swatch: 'black' }
    ],
    frame_colour: [
      { value: 'Grey', frame: 'grey' }, { value: 'Dark Brown', frame: 'brown' },
      { value: 'White', frame: 'white' }, { value: 'Black', frame: 'black' },
      { value: 'Help me choose', label: 'Help me choose', frame: 'help' }
    ],
    operation: [
      { value: 'Motorised', note: 'Convenient everyday operation' },
      { value: 'Spring-assisted manual push', label: 'Manual push', note: 'Spring-assisted manual operation' },
      { value: 'Bead chain', note: 'Manual chain operation' },
      { value: 'Help me choose', note: 'Let our team recommend a suitable system' }
    ],
    control_preference: ['Individually', 'Together as a group', 'Help me choose']
  };
  const diagrams = [
    '<path d="M7 24V7h38v17"/><path stroke-dasharray="2 4" opacity=".3" d="M7 24h38"/>',
    '<path d="M12 7v19h31"/><path stroke-dasharray="2 4" opacity=".3" d="M12 7h31v19"/>',
    '<path d="M9 7v19h34V7"/><path stroke-dasharray="2 4" opacity=".3" d="M9 7h34"/>',
    '<path d="M20 10a6 6 0 0 1 12 0c0 5-6 5-6 10"/><path d="M26 26h.01"/>'
  ];

  function node(tag, className, content) {
    const el = document.createElement(tag);
    if (className) el.className = className;
    if (content !== undefined) el.textContent = content;
    return el;
  }
  Object.entries(options).forEach(([name, choices]) => {
    const container = root.querySelector(`[data-options="${name}"]`);
    choices.forEach((choice, index) => {
      const item = typeof choice === 'string' ? { value: choice } : choice;
      const label = node('label', 'ls-option');
      const input = node('input');
      Object.assign(input, { type: 'radio', name, value: item.value, required: true });
      const content = node('span', 'ls-option-content');
      if (name === 'layout') {
        const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
        svg.setAttribute('viewBox', '0 0 52 34');
        svg.setAttribute('aria-hidden', 'true');
        svg.innerHTML = diagrams[index];
        content.append(svg);
      }
      if (item.swatch) content.append(node('span', `ls-swatch ls-swatch-${item.swatch}`));
      if (item.frame) {
        const dot = node('span', `ls-frame-dot ls-frame-${item.frame}`, item.frame === 'help' ? '?' : '');
        dot.setAttribute('aria-hidden', 'true');
        content.append(dot);
      }
      content.append(node('strong', '', item.label || item.value));
      if (item.note) content.append(node('small', '', item.note));
      label.append(input, content);
      container.append(label);
    });
  });

  let activeStep = 0;
  let openings = [];
  const visited = new Set([0]);
  const get = name => {
    const control = form.elements.namedItem(name);
    return typeof control?.value === 'string' ? control.value.trim() : '';
  };
  function set(name, value) {
    const control = form.elements.namedItem(name);
    if (control && 'value' in control) control.value = value;
  }
  function clearRadio(name) {
    form.querySelectorAll(`input[name="${name}"]`).forEach(input => { input.checked = false; });
  }
  function baseCount() { return get('layout') === 'Three-sided' ? 3 : get('layout') === 'L-shaped' ? 2 : 1; }
  function makeOpening(index) { return { label: `Opening ${index + 1}`, width_mm: '', height_mm: '' }; }
  function resizeOpenings(count) {
    while (openings.length < count) openings.push(makeOpening(openings.length));
    openings = openings.slice(0, count);
    renderOpenings();
  }
  function renderOpenings() {
    const list = $('#openings-list');
    list.replaceChildren();
    openings.forEach((opening, index) => {
      const article = node('div', 'ls-opening');
      const heading = node('div', 'ls-opening-heading');
      const labelField = node('div', 'ls-field');
      const label = node('label', '', `Opening ${index + 1} · name`);
      const nameInput = node('input');
      nameInput.id = `opening-${index}-label`;
      nameInput.value = opening.label;
      nameInput.maxLength = 50;
      label.htmlFor = nameInput.id;
      nameInput.addEventListener('input', () => { opening.label = nameInput.value; refreshSummary(); });
      labelField.append(label, nameInput);
      heading.append(labelField);
      if (index >= baseCount()) {
        const remove = node('button', 'ls-remove-opening', 'Remove');
        remove.type = 'button';
        remove.setAttribute('aria-label', `Remove opening ${index + 1}`);
        remove.addEventListener('click', () => { openings.splice(index, 1); renderOpenings(); refreshSummary(); });
        heading.append(remove);
      }
      const fields = node('div', 'ls-opening-size');
      ['width', 'height'].forEach(axis => {
        const field = node('div', 'ls-field');
        const text = node('label', '', `${axis === 'width' ? 'Width' : 'Height'} (mm)`);
        const input = node('input');
        Object.assign(input, { type: 'number', id: `opening-${index}-${axis}`, min: '1', step: '1', placeholder: axis === 'width' ? 'e.g. 2800' : 'e.g. 2600', value: opening[`${axis}_mm`] });
        input.inputMode = 'numeric';
        text.htmlFor = input.id;
        input.addEventListener('input', () => {
          opening[`${axis}_mm`] = input.value;
          updateSizeNote(index);
          refreshSummary();
        });
        field.append(text, input);
        fields.append(field);
      });
      const note = node('p', 'ls-opening-note');
      note.id = `opening-note-${index}`;
      note.setAttribute('aria-live', 'polite');
      article.append(heading, fields, note);
      list.append(article);
      updateSizeNote(index);
    });
    $('#add-opening').hidden = openings.length >= 8;
  }
  function updateSizeNote(index) {
    const opening = openings[index];
    const note = $(`#opening-note-${index}`);
    const width = Number(opening.width_mm), height = Number(opening.height_mm);
    note.textContent = width > 4000 || height > 3000
      ? 'Outside the published LS-100 range. Your measurements are saved for our team to assess.'
      : width > 0 && height > 0 ? `Approximate opening area: ${(width * height / 1000000).toFixed(2)} m²` : '';
  }
  function syncDependencies(changedName) {
    if (changedName === 'layout') resizeOpenings(baseCount());
    $('#opening-fields').hidden = get('measurement_status') !== 'approximate';
    $('#measurement-note').hidden = get('measurement_status') !== 'home_measurement';
    const specificFabric = get('fabric_id') === 'blackout';
    $('#fabric-colours').hidden = !specificFabric;
    if (!specificFabric) clearRadio('fabric_colour_id');
    const motorised = get('operation') === 'Motorised';
    $('#motor-options').hidden = !motorised;
    if (!motorised) {
      clearRadio('control_preference');
      set('app_control_interest', '');
      set('rain_sensor_interest', '');
    }
    refreshSummary();
  }
  function clearError() {
    $('#form-error').hidden = true;
    form.querySelectorAll('[aria-invalid]').forEach(el => el.removeAttribute('aria-invalid'));
  }
  form.addEventListener('input', () => { clearError(); refreshSummary(); });
  form.addEventListener('change', event => { clearError(); syncDependencies(event.target.name); });
  $('#add-opening').addEventListener('click', () => {
    if (openings.length >= 8) return;
    openings.push(makeOpening(openings.length));
    renderOpenings(); refreshSummary();
    $(`#opening-${openings.length - 1}-width`).focus();
  });

  function invalid(step) {
    const need = (name, message) => !get(name) ? { message, target: form.querySelector(`[name="${name}"]`) } : null;
    if (step === 0) return need('space_type', 'Choose your installation space, or select Not sure.') || need('exposure', 'Choose an installation environment, or select Not sure.') || need('layout', 'Choose an opening layout, or select Not sure.');
    if (step === 1) {
      const missing = need('measurement_status', 'Choose whether you have approximate sizes or need a home measurement.');
      if (missing) return missing;
      if (get('measurement_status') === 'approximate') {
        for (let index = 0; index < openings.length; index++) {
          for (const axis of ['width', 'height']) {
            const number = Number(openings[index][`${axis}_mm`]);
            if (!Number.isFinite(number) || !Number.isInteger(number) || number <= 0)
              return { message: `Enter a positive whole-number ${axis} in mm for opening ${index + 1}.`, target: $(`#opening-${index}-${axis}`) };
          }
        }
      }
      const sets = Number(get('identical_sets'));
      if (!Number.isInteger(sets) || sets < 1 || sets > 99) return { message: 'Enter 1 to 99 identical sets.', target: $('#identical_sets') };
    }
    if (step === 2) return need('shade_priority', 'Choose your main priority, or select Help me choose.') || need('fabric_id', 'Choose a fabric collection, or select Help me choose.') || (get('fabric_id') === 'blackout' && need('fabric_colour_id', 'Choose one of the blackout fabric colours.')) || need('frame_colour', 'Choose a frame colour, or select Help me choose.');
    if (step === 3) return need('operation', 'Choose an operating system, or select Help me choose.') || (get('operation') === 'Motorised' && need('control_preference', 'Choose individual or grouped control, or select Help me choose.'));
    if (step === 4) {
      const missing = need('customer_name', 'Please enter your full name.') || need('phone', 'Please enter your phone or WhatsApp number.') || need('email', 'Please enter your email address.');
      if (missing) return missing;
      const digits = get('phone').replace(/\D/g, '');
      if (!/^[+\d\s().-]+$/.test(get('phone')) || digits.length < 8 || digits.length > 15) return { message: 'Enter a valid phone number, including country code if applicable.', target: $('#phone') };
      if (!$('#email').validity.valid) return { message: 'Please enter a valid email address.', target: $('#email') };
      if (get('postal_code') && !/^\d{6}$/.test(get('postal_code'))) return { message: 'A Singapore postal code should contain 6 digits.', target: $('#postal_code') };
    }
    return null;
  }
  function showError(issue) {
    $('#form-error').textContent = issue.message;
    $('#form-error').hidden = false;
    issue.target?.setAttribute('aria-invalid', 'true');
    issue.target?.focus();
  }
  function showStep(index, focus = true) {
    activeStep = Math.max(0, Math.min(4, index));
    visited.add(activeStep);
    clearError();
    panels.forEach((panel, i) => { panel.hidden = i !== activeStep; });
    stepButtons.forEach((button, i) => {
      if (i === activeStep) button.setAttribute('aria-current', 'step'); else button.removeAttribute('aria-current');
      button.classList.toggle('is-complete', visited.has(i) && !invalid(i) && i !== activeStep);
    });
    $('#back-button').hidden = activeStep === 0;
    $('#next-button').hidden = activeStep === 4;
    $('#quote-button').hidden = activeStep !== 4;
    if (focus) panels[activeStep].querySelector('h3').focus({ preventScroll: true });
    if (focus && window.matchMedia('(max-width:820px)').matches) $('.ls-form-shell').scrollIntoView({ behavior: 'instant', block: 'start' });
  }
  stepButtons.forEach(button => button.addEventListener('click', () => showStep(Number(button.dataset.stepLink))));
  $('#next-button').addEventListener('click', () => { const issue = invalid(activeStep); if (issue) showError(issue); else showStep(activeStep + 1); });
  $('#back-button').addEventListener('click', () => showStep(activeStep - 1));

  function readableFabric() {
    if (get('fabric_id') === 'help') return 'Help me choose';
    if (get('fabric_id') !== 'blackout') return 'To be selected';
    const fabric = options.fabric_colour_id.find(item => item.value === get('fabric_colour_id'));
    return fabric ? `Blackout · ${fabric.label} · ${fabric.value}` : 'Blackout · colour to be selected';
  }
  function readableOpenings() {
    if (get('measurement_status') === 'home_measurement') return 'Home measurement requested';
    if (get('measurement_status') !== 'approximate') return 'Sizes or home measurement';
    return openings.map((opening, index) => `${opening.label.trim() || `Opening ${index + 1}`}: ${opening.width_mm || '—'} × ${opening.height_mm || '—'} mm`).join('\n');
  }
  function appendRow(dl, label, value) {
    const row = node('div');
    row.append(node('dt', '', label), node('dd', '', value));
    dl.append(row);
  }
  function refreshSummary() {
    const list = $('#live-summary');
    list.replaceChildren();
    appendRow(list, 'Space', [get('space_type'), get('layout')].filter(Boolean).join(' · ') || 'To be selected');
    appendRow(list, 'Sizes', readableOpenings());
    list.lastElementChild.querySelector('dd').style.whiteSpace = 'pre-line';
    appendRow(list, 'Fabric', readableFabric());
    appendRow(list, 'Frame', get('frame_colour') || 'To be selected');
    appendRow(list, 'Operation', get('operation') || 'To be selected');
    if (get('operation') === 'Motorised' && get('control_preference')) appendRow(list, 'Controls', get('control_preference'));
    appendRow(list, 'Identical sets', get('identical_sets') || '—');
    const complete = [0, 1, 2, 3, 4].filter(step => !invalid(step)).length;
    $('#summary-status').textContent = complete ? `${complete} of 5 steps ready` : 'Let’s get started';
  }
  function configuration() {
    return {
      product_handle: 'ls-100-zip-blind', product: 'LS-100 Zip Blind', space_type: get('space_type'), exposure: get('exposure'), layout: get('layout'), measurement_status: get('measurement_status'),
      openings: get('measurement_status') === 'approximate' ? openings.map((opening, i) => ({ label: opening.label.trim() || `Opening ${i + 1}`, width_mm: Number(opening.width_mm), height_mm: Number(opening.height_mm), needs_assessment: Number(opening.width_mm) > 4000 || Number(opening.height_mm) > 3000 })) : [],
      identical_sets: Number(get('identical_sets')), shade_priority: get('shade_priority'), fabric_id: get('fabric_id'), fabric_openness: get('fabric_id') === 'blackout' ? 0 : null, fabric_colour_id: get('fabric_colour_id') || null,
      frame_colour: get('frame_colour'), custom_finish_request: get('custom_finish_request'), operation: get('operation'),
      control_preference: get('operation') === 'Motorised' ? get('control_preference') : null,
      app_control_interest: get('operation') === 'Motorised' ? get('app_control_interest') || null : null,
      rain_sensor_interest: get('operation') === 'Motorised' ? get('rain_sensor_interest') || null : null,
      customer_name: get('customer_name'), phone: get('phone'), email: get('email'), postal_code: get('postal_code'), project_name: get('project_name'), customer_notes: get('customer_notes')
    };
  }
  form.addEventListener('submit', event => {
    event.preventDefault();
    for (let step = 0; step < panels.length; step++) {
      const issue = invalid(step);
      if (issue) { showStep(step); showError(issue); return; }
    }
    const data = configuration();
    const summary = $('#review-summary');
    summary.replaceChildren();
    const rows = [
      ['Product', data.product], ['Space', `${data.space_type} · ${data.exposure}`], ['Layout', data.layout], ['Measurement status', data.measurement_status], ['Openings', readableOpenings()], ['Identical sets', String(data.identical_sets)],
      ['Main priority', data.shade_priority], ['Fabric', readableFabric()], ['Frame colour', data.frame_colour], ['Operation', data.operation]
    ];
    if (data.openings.some(opening => opening.needs_assessment)) rows.push(['Size assessment', 'At least one opening exceeds the published LS-100 range. Team assessment requested.']);
    if (data.control_preference) rows.push(['Controls', data.control_preference], ['App control', data.app_control_interest || 'No preference given'], ['Rain sensor', data.rain_sensor_interest || 'No preference given']);
    if (data.custom_finish_request) rows.push(['Special finish', data.custom_finish_request]);
    rows.push(['Full name', data.customer_name], ['Phone / WhatsApp', data.phone], ['Email', data.email]);
    if (data.postal_code) rows.push(['Postal code', data.postal_code]);
    if (data.project_name) rows.push(['Project', data.project_name]);
    if (data.customer_notes) rows.push(['Notes', data.customer_notes]);
    form.dataset.lsInquiryConfiguration = JSON.stringify(data);
    rows.forEach(([label, value]) => appendRow(summary, label, value));
    $('#ls100-contact-name').value = data.customer_name;
    $('#ls100-contact-email').value = data.email;
    $('#ls100-contact-phone').value = data.phone;
    $('#ls100-contact-configuration').value = rows.map(([label, value]) => `${label}: ${value}`).join('\n');
    $('#review-dialog').showModal();
    refreshSummary();
  });
  ['#close-dialog', '#edit-plan'].forEach(selector => $(selector).addEventListener('click', () => $('#review-dialog').close()));
  $('#review-dialog').addEventListener('click', event => { if (event.target === $('#review-dialog')) { const rect = event.target.getBoundingClientRect(); if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) event.target.close(); } });

  openings = [makeOpening(0)];
  renderOpenings();
  syncDependencies();
  showStep(0, false);
})();
'use strict';
(() => {
  const root = document.querySelector('#ls100-product');
  if (!root) return;
  const main = root.querySelector('#main-product-image');
  const photoDialog = root.querySelector('#product-photo-dialog');
  const enlarged = root.querySelector('#enlarged-product-image');
  root.querySelectorAll('[data-gallery]').forEach(button => {
    button.addEventListener('click', () => {
      main.src = button.dataset.gallery;
      main.alt = button.dataset.alt;
      root.querySelectorAll('[data-gallery]').forEach(item => item.setAttribute('aria-pressed', String(item === button)));
    });
  });
  root.querySelector('#zoom-product').addEventListener('click', () => {
    enlarged.src = main.src; enlarged.alt = main.alt; photoDialog.showModal();
  });
  root.querySelector('#close-photo-dialog').addEventListener('click', () => photoDialog.close());
  root.querySelectorAll('[data-choose-fabric]').forEach(button => {
    button.addEventListener('click', () => {
      root.querySelector('[data-step-link="2"]').click();
      root.querySelector('input[name="fabric_id"][value="blackout"]').click();
      root.querySelector(`input[name="fabric_colour_id"][value="${button.dataset.chooseFabric}"]`).click();
      root.querySelector('#customise').scrollIntoView({behavior:'instant',block:'start'});
      root.querySelector('#finish-heading').focus({preventScroll:true});
    });
  });
})();
